
/**
 * Document Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

function formatControlColumn(doc) {
    const rc = doc.relatedControls;
    if (!Array.isArray(rc) || rc.length === 0) return '-';
    const parts = rc.map((c) => {
        if (!c || typeof c !== 'object') return '';
        return c.controlCode || c.control || c.name || '';
    }).filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
}

function firstOwnerUserIdFromParty(party) {
    if (!party || typeof party !== 'object') return '';
    const rows = party.assignments || [];
    for (const a of rows) {
        if (
            String(a?.roleKey || '').toUpperCase() === 'OWNER' &&
            String(a?.assigneeType || '').toLowerCase() === 'user' &&
            a?.assigneeId != null
        ) {
            return String(a.assigneeId);
        }
    }
    return '';
}

function resolveDocumentTypeLabel(doc) {
    const dt = doc?.documentType;
    if (dt && typeof dt === 'object') {
        const label = dt.documentCategory || dt.name || dt.documentTypeAbbreviation;
        if (label) return String(label);
    }
    const legacy = doc?.type;
    if (legacy && typeof legacy === 'object') {
        const label = legacy.documentCategory || legacy.name || legacy.documentTypeAbbreviation;
        if (label) return String(label);
    }
    if (typeof legacy === 'string' && legacy.trim() && !/^[a-f0-9]{24}$/i.test(legacy.trim())) {
        return legacy.trim();
    }
    return '';
}

function mapDocumentListItem(doc) {
    const rev = doc.currentRevision;
    const hasRev = rev && typeof rev === 'object' && (rev._id || rev.fileType);
    const hasDynamicRev = Boolean(hasRev && (rev.reportTemplate || rev.isDynamic));
    const fromDynamicSource = Boolean(doc.dynamicReportSource);
    const reportKey = doc.reportBinding?.reportKey ? String(doc.reportBinding.reportKey) : '';
    const catalogReport = Boolean(reportKey);
    const contentSource = catalogReport
        ? 'catalog-report'
        : fromDynamicSource || hasDynamicRev
            ? 'dynamic'
            : hasRev
                ? 'file'
                : 'empty';
    const docTypeId = doc.documentType?._id || doc.documentType?.id || doc.documentType;
    const classificationId = doc.classification?._id || doc.classification?.id || doc.classification;
    const ownerId =
        firstOwnerUserIdFromParty(doc.partyInstanceId) ||
        doc.owner?._id ||
        doc.owner?.id ||
        doc.owner ||
        '';
    const unitId = doc.unit?._id || doc.unit?.id || doc.unit;
    const assignmentTemplateRef = doc.assignmentTemplate;
    const assignmentTemplateId = assignmentTemplateRef?._id || assignmentTemplateRef?.id || assignmentTemplateRef;
    return {
        ...doc,
        ID: doc._id,
        id: doc._id,
        documentName: doc.title,
        reportName: doc.title,
        type: resolveDocumentTypeLabel(doc) || '-',
        code: doc.code || '-',
        classificationLevel: doc.classification?.level || doc.classificationLevel || '-',
        version: doc.version || '1.0',
        author: doc.createdBy?.firstName
            ? `${doc.createdBy.firstName} ${doc.createdBy.lastName}`
            : (doc.owner?.firstName
                ? `${doc.owner.firstName} ${doc.owner.lastName}`
                : doc.author || '-'),
        unit: doc.unit?.name || doc.unit || '-',
        currentRevision: doc.currentRevision,
        isActive: doc.status === 'active' || doc.isActive !== false,
        control: formatControlColumn(doc),
        reportTemplate: hasRev ? rev.reportTemplate : undefined,
        dynamicReport: fromDynamicSource || hasDynamicRev,
        reportAvailable: catalogReport || Boolean(hasRev && rev.reportTemplate),
        documentAvailable: Boolean(hasRev),
        reportBinding: catalogReport ? { reportKey } : null,
        catalogReport,
        reportKey,
        contentSource,
        hasReportDraft: Boolean(doc.dynamicReportDraft?.hasDraft),
        documentTypeId: docTypeId ? String(docTypeId) : '',
        classificationLevelId: classificationId ? String(classificationId) : '',
        ownerId: ownerId ? String(ownerId) : '',
        unitId: unitId ? String(unitId) : '',
        assignmentTemplateId: assignmentTemplateId ? String(assignmentTemplateId) : '',
    };
}

function mapDocumentDetail(doc) {
    const reportKey = doc.reportBinding?.reportKey ? String(doc.reportBinding.reportKey) : '';
    const catalogReport = Boolean(reportKey);
    const assignmentTemplateRef = doc.assignmentTemplate;
    const assignmentTemplateId = assignmentTemplateRef?._id || assignmentTemplateRef?.id || assignmentTemplateRef || '';
    return {
        ...doc,
        ID: doc._id,
        id: doc._id,
        documentName: doc.title,
        type: resolveDocumentTypeLabel(doc) || '-',
        classificationLevel: doc.classification?.level || doc.classificationLevel || '-',
        author: doc.createdBy?.firstName
            ? `${doc.createdBy.firstName} ${doc.createdBy.lastName}`
            : (doc.owner?.firstName
                ? `${doc.owner.firstName} ${doc.owner.lastName}`
                : doc.author || '-'),
        unit: doc.unit?.name || doc.unit || '-',
        isActive: doc.status === 'active' || doc.isActive !== false,
        currentRevision: doc.currentRevision,
        reportAvailable: catalogReport || Boolean(doc.currentRevision?.reportTemplate),
        reportBinding: catalogReport ? { reportKey } : null,
        catalogReport,
        reportKey,
        contentSource: catalogReport
            ? 'catalog-report'
            : doc.dynamicReportSource || doc.currentRevision?.reportTemplate || doc.currentRevision?.isDynamic
                ? 'dynamic'
                : doc.currentRevision
                    ? 'file'
                    : 'empty',
        documentTypeId: doc.documentType?._id || doc.documentType?.id || doc.documentType || '',
        classificationLevelId: doc.classification?._id || doc.classification?.id || doc.classification || '',
        ownerId:
            firstOwnerUserIdFromParty(doc.partyInstanceId) ||
            doc.owner?._id ||
            doc.owner?.id ||
            doc.owner ||
            '',
        unitId: doc.unit?._id || doc.unit?.id || doc.unit || '',
        assignmentTemplateId: assignmentTemplateId ? String(assignmentTemplateId) : '',
    };
}

function buildListQueryParams(page, pageSize, search, sortBy, sortOrder, columnFilters, extra) {
    const params = {
        page,
        pageSize,
        sortOrder: sortOrder || 'asc',
        ...(search ? { search } : {}),
        ...(sortBy ? { sortBy } : {}),
    };
    const { excludeIds, designerOnly, relatedVendorId } = extra || {};
    if (excludeIds) {
        params.excludeIds = Array.isArray(excludeIds) ? excludeIds.join(',') : String(excludeIds);
    }
    if (designerOnly) {
        params.designerOnly = '1';
    }
    if (relatedVendorId) {
        params.relatedVendorId = String(relatedVendorId);
    }
    for (const [k, v] of Object.entries(columnFilters || {})) {
        if (v === undefined || v === null || v === '') continue;
        const key = k.startsWith('filter_') ? k : `filter_${k}`;
        params[key] = v;
    }
    return params;
}

function normalizeListResponse(data, page, pageSize) {
    const rawList = Array.isArray(data?.data)
        ? data.data
        : (data?.data?.results || data?.data?.documents);
    if (!data?.success || rawList == null) {
        return data;
    }
    const apiDocs = Array.isArray(rawList) ? rawList : [];
    const mappedDocs = apiDocs.map(mapDocumentListItem);

    const pendingCreateRows = (data.pendingCreateRows || []).map((item) => ({
        ...item,
        id: item.pendingChangeId,
        _rowSource: 'pendingCreate',
        documentName: item.documentName || item.title || '-',
        type: item.type || '-',
        code: item.code || '-',
        classificationLevel: item.classificationLevel || '-',
        version: item.version || '1.0',
        dynamicReport: Boolean(item.dynamicReportSource || item.isDynamic),
        reportAvailable: false,
        documentAvailable: false,
        reportBinding: null,
        catalogReport: false,
        reportKey: '',
        contentSource: 'pending',
        hasReportDraft: false,
    }));

    const totalItems = data.pagination?.total ?? mappedDocs.length;
    const totalPages = data.pagination?.totalPages || Math.ceil(totalItems / pageSize) || 1;

    return {
        success: true,
        data: {
            documents: [...pendingCreateRows, ...mappedDocs],
            pagination: {
                currentPage: page,
                totalPages,
                totalItems,
                itemsPerPage: pageSize,
                hasNextPage: page < totalPages,
                hasPreviousPage: page > 1,
            },
            uniqueValues: data.uniqueValues || {},
            scope: data.scope || "full",
        },
        message: data.message || 'Documents fetched successfully from Server',
    };
}

function normalizeMutationResult(status, resultBody) {
    const result = resultBody && typeof resultBody === 'object' ? resultBody : {};
    const pendingChangeId = result.pendingChangeId || result.data?.pendingChangeId || null;
    const pendingApproval = Boolean(pendingChangeId || status === 202);
    return {
        ...result,
        success: result.success !== false,
        pendingApproval,
        pendingChangeId,
    };
}

function buildCreateMetadata(data) {
    let finalApprovalStatus = data.approvalStatus || 'draft';
    if (finalApprovalStatus === 'none') finalApprovalStatus = 'draft';

    const metadata = {
        title: data.title,
        code: data.code || '',
        description: data.description || '',
        version: data.version || '1.0',
        documentType: data.documentType || null,
        classification: data.classification || null,
        assignmentTemplate: data.assignmentTemplate || null,
        unit: data.unit || null,
        status: 'active',
        approvalStatus: finalApprovalStatus,
        ...(data.reviewFrequency ? { reviewFrequency: data.reviewFrequency } : {}),
        ...(data.sourceType === 'dynamic' ? { dynamicReportSource: true } : {}),
        ...(data.sourceType === 'catalog-report' && data.reportKey
            ? { reportBinding: { reportKey: String(data.reportKey).trim() } }
            : {}),
    };

    Object.keys(metadata).forEach((key) => {
        if (metadata[key] === '' || metadata[key] === null) {
            delete metadata[key];
        }
    });
    return metadata;
}

function sanitizeUpdatePayload(data) {
    const payload = { ...data };
    delete payload.file;
    delete payload.sourceType;
    delete payload.owner;
    if (Array.isArray(payload.relatedVendors)) {
        payload.relatedVendors = [...new Set(payload.relatedVendors.map((id) => String(id).trim()).filter(Boolean))];
    }
    for (const key of ['documentType', 'classification', 'unit', 'assignmentTemplate']) {
        if (
            payload[key] === ''
            || payload[key] === '-'
            || payload[key] === 'undefined'
            || payload[key] === 'null'
            || payload[key] === '[object Object]'
        ) {
            delete payload[key];
        }
    }
    return payload;
}

export class DocumentService {
    constructor(api, config = {}) {
        this.api = api;
        this.documentRevisionApi = config.documentRevisionApi || null;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 },
            );
            if (response?.focus) {
                return {
                    success: true,
                    focus: response.focus,
                };
            }
            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues,
                message: response.message || 'Document listesi getirildi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch document list');
        }
    }

    /**
     * Report operations list: mapped rows, pagination, uniqueValues (UI shape).
     */
    async fetchReportDocumentList({
        page = 1,
        pageSize = 10,
        search = '',
        sortBy = '',
        sortOrder = 'asc',
        columnFilters = {},
        extra = {},
    } = {}) {
        try {
            const params = buildListQueryParams(page, pageSize, search, sortBy, sortOrder, columnFilters, extra);
            const raw = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 },
            );
            return normalizeListResponse(raw, page, pageSize);
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch documents');
        }
    }

    async fetchCatalogReportUsage() {
        try {
            const response = await withRetry(
                () => this.api.getCatalogReportUsage(),
                { maxRetries: 2 },
            );
            return {
                success: response?.success !== false,
                data: response?.data || { reportKeys: [] },
                message: response?.message,
                error: response?.error,
                code: response?.code,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch catalog report usage');
        }
    }

    async createReportDesignerCapability(id) {
        try {
            const response = await this.api.createReportDesignerCapability(id);
            if (response?.success === false || !response?.data?.token) throw new Error(response?.error || 'Designer capability could not be created');
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to create report designer capability');
        }
    }

    async getPublicCatalog() {
        try {
            const response = await this.api.getPublicCatalog();
            return {
                success: response?.success !== false,
                data: Array.isArray(response?.data) ? response.data : [],
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch public documents');
        }
    }

    async getPublicShare(id) {
        try {
            const response = await this.api.getPublicShare(id);
            return {
                success: response?.success !== false,
                data: response?.data,
                message: response?.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch public share configuration');
        }
    }

    async updatePublicShare(id, body) {
        try {
            const response = await this.api.updatePublicShare(id, body);
            return {
                success: response?.success !== false,
                data: response?.data,
                message: response?.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update public share configuration');
        }
    }

    async runReport(id, input = {}) {
        try {
            const response = await this.api.runReport(id, input);
            return {
                success: response?.success !== false,
                data: response?.data,
                message: response?.message,
                error: response?.error,
                code: response?.code,
                retryable: response?.retryable,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to run document report');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 },
            );
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch document');
        }
    }

    async getMappedById(id) {
        const res = await this.getById(id);
        if (res.success && res.data) {
            return {
                ...res,
                data: mapDocumentDetail(res.data),
            };
        }
        return res;
    }

    async create(data) {
        try {
            const response = await this.api.create(data);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Document oluşturuldu',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create document');
        }
    }

    /**
     * Create metadata via JSON then optional first revision upload (FormData).
     * @param {Function|{ onUploadProgress?: Function }} [optionsOrCallback]
     */
    async createWithOptionalFile(data, optionsOrCallback) {
        const metadata = buildCreateMetadata(data);
        try {
            const { status, data: docResult } = await this.api.createWithResponse(metadata);
            const docId = docResult?.data?._id || docResult?.data?.id;

            if (data.file && docId && this.documentRevisionApi) {
                const formData = new FormData();
                formData.append('file', data.file);
                formData.append('description', 'Initial version');
                try {
                    await this.documentRevisionApi.createRevision(docId, formData, optionsOrCallback);
                } catch (revErr) {
                    const msg = revErr?.message || revErr?.response?.data?.error || 'Bilinmeyen hata';
                    return {
                        success: false,
                        error: `Doküman kaydı oluşturuldu ancak dosya revizyonu yüklenemedi: ${msg}`,
                        data: docResult?.data,
                        documentCreated: true,
                        ...normalizeMutationResult(status, docResult),
                    };
                }
            }
            return normalizeMutationResult(status, docResult);
        } catch (error) {
            throw this._handleError(error, 'Failed to create document');
        }
    }

    async update(id, data) {
        try {
            const response = await this.api.update(id, data);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Document güncellendi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update document');
        }
    }

    async updateDocument(id, data) {
        const payload = sanitizeUpdatePayload(data);
        try {
            const { status, data: body } = await this.api.updateWithResponse(id, payload);
            return normalizeMutationResult(status, body);
        } catch (error) {
            throw this._handleError(error, 'Failed to update document');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Document silindi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete document');
        }
    }

    async deleteDocument(id) {
        try {
            const { status, data: body } = await this.api.deleteWithResponse(id);
            return normalizeMutationResult(status, body);
        } catch (error) {
            throw this._handleError(error, 'Failed to delete document');
        }
    }

    async associateAssignmentTemplates(documentId, { flowIds }) {
        try {
            const json = await this.api.associateAssignmentTemplates(documentId, { flowIds });
            return json;
        } catch (error) {
            throw this._handleError(error, 'Failed to associate assignment templates');
        }
    }

    async markDynamicReportDraft(documentId) {
        try {
            return await this.api.markDynamicReportDraft(documentId);
        } catch (error) {
            throw this._handleError(error, 'Failed to mark report draft');
        }
    }

    async seedDynamicReportTemplate(documentId, body = {}) {
        try {
            return await this.api.seedDynamicReportTemplate(documentId, body);
        } catch (error) {
            throw this._handleError(error, 'Failed to seed report template');
        }
    }

    async publishDynamicReportDraft(documentId, body = {}) {
        try {
            return await this.api.publishDynamicReportDraft(documentId, body);
        } catch (error) {
            throw this._handleError(error, 'Failed to publish report draft');
        }
    }

    /**
     * DependencySelectorDialog: mapped { data: items, total }.
     */
    async getAllForDependencySelector(params = {}) {
        const response = await this.fetchReportDocumentList({
            page: params.page,
            pageSize: params.pageSize,
            search: params.search,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            columnFilters: params.columnFilters,
            extra: {
                excludeIds: params.excludeIds,
                relatedVendorId: params.relatedVendorId,
            },
        });

        if (response.success && response.data?.documents) {
            const mappedItems = response.data.documents.map((doc) => ({
                ...doc,
                id: doc.ID,
                name: doc.documentName,
                description: doc.description,
                type: doc.type,
                author: doc.author,
                expiryDate: doc.expiryDate,
                location: doc.location,
                accessPermissions: doc.accessPermissions,
                unit: doc.unit,
            }));

            return {
                data: mappedItems,
                total: response.data.pagination.totalItems,
            };
        }

        return { data: [], total: 0 };
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getPartyTemplate(params),
                { maxRetries: 2 }
            );
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch document party template');
        }
    }

    async getParty(id) {
        try {
            const response = await withRetry(
                () => this.api.getParty(id),
                { maxRetries: 2 }
            );
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch document party');
        }
    }

    async syncParty(id, payload) {
        try {
            const response = await this.api.syncParty(id, payload);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync document party');
        }
    }

    async setApprovers(id, approvers) {
        try {
            const response = await this.api.setApprovers(id, approvers);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to set document approvers');
        }
    }

    async setApprovalMode(id, mode) {
        try {
            const response = await this.api.setApprovalMode(id, mode);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to set document approval mode');
        }
    }

    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`,
        };
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
